/**
 * Decorative bottom strip: renders the `frogger.bottom` PNG along the bottom
 * edge of the screen and overlays a row of translatable legend cards — three
 * power-ups (dash / bomb / slow) followed by two collectibles (star / coin)
 * so the player sees, at a glance, what each pickup does and how many points
 * it is worth.
 *
 * Lives in screen-fixed space (sits in the scene container behind
 * worldContainer) so the player and vehicles render *on top* of the strip
 * without covering the cards (cards are bottom-aligned and stay clear of the
 * player's screen position).
 *
 * Auto-fades out as the player climbs away from the start area so it doesn't
 * stay glued to the bottom for the whole run.
 *
 * Card contents are read from i18n at construction time:
 *   frogger.power_<id>_title    — short uppercase label    (power-ups)
 *   frogger.power_<id>_desc     — one-line description     (power-ups)
 *   frogger.collect_<id>_title  — short uppercase label    (collectibles)
 *   frogger.collect_<id>_desc   — one-line description, supports {p}
 *                                 point-value interpolation
 */

import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { t } from '../../config/i18n.js';
import { FROGGER_CONFIG } from './froggerConfig.js';
import { paintChip, darken } from '../../ui/shared.js';

const STRIP_HEIGHT = 272;
// Card width has to clear the longest HU description, currently
// "Lassú forgalom 5 mp-ig" (LASSÍTÁS card) — at Poppins-600 14px
// that's ~170px, plus the 74px icon medallion lane and 8px right
// gutter, so 280 leaves room without horizontal squeeze.
// Sizes are deliberately scaled down from the original 320/96 so
// the row clears the on-screen D-pads in `FroggerControls`
// (200px wide, 24px margins — i.e. left pad ends at x=224, right
// pad starts at x=1696). At 280/220 with 24px gap the centred
// row spans x=272..1648, leaving ~48px of breathing room each
// side so the outermost cards are no longer covered by the pads.
const CARD_W = 280;
// Collectible cards (STAR / COIN) carry a shorter description and only
// need to fit a single short line like "Grab them! +100", so we
// give them a narrower width to keep all five cards on one row.
const COLLECT_CARD_W = 220;
const CARD_H = 84;
const CARD_GAP = 24;
// Sharp-cornered, shadow-less footer cards — per UX direction the
// SPRINT / BOMBA / LASSÍTÁS strip drops both rounded corners and the
// drop shadow so the bottom row visually matches the squared-off
// in-game top header (see `FroggerTopBar`'s `CHIP_RADIUS`).
const CARD_RADIUS = 0;
const ICON_SIZE = 50;
const CARDS_BOTTOM_PAD = 28;
// Icon medallion + text x-anchors (scaled to match the smaller
// card height / icon). The medallion is centred at ICON_CX with
// radius ICON_SIZE/2 + 5; text columns start at TEXT_X with a
// ~10px gap from the medallion's right edge.
const ICON_CX = 34;
const TEXT_X = 74;

/** Set both to a high number (or equal) to keep the strip fully visible at
 * all times. Lower values fade the strip out as the player climbs away. */
const FADE_START_ROW = Infinity;
const FADE_END_ROW = Infinity;

const POWER_UPS = [
    { id: 'dash',  iconSlot: 'frogger.ability.dash', color: 0x3b82f6 },
    { id: 'bomb',  iconSlot: 'frogger.ability.bomb', color: 0xec4899 },
    { id: 'slow',  iconSlot: 'frogger.ability.slow', color: 0xa855f7 },
];

// Star + Coin legend cards — placed to the right of the power-up cards
// so players can see at a glance which pickups are worth grabbing and
// for how many points. Point values are pulled live from
// `FROGGER_CONFIG` (`STAR_POINTS`, `COIN_POINTS`) via the `{p}` token
// in the i18n description string, so the cards stay in sync with the
// scoring config without duplicating the numbers here.
const COLLECTIBLES = [
    {
        id: 'star',
        iconSlot: 'frogger.collectible.star',
        color: 0xec4899,
        points: FROGGER_CONFIG.STAR_POINTS,
        titleKey: 'frogger.collect_star_title',
        descKey: 'frogger.collect_star_desc',
    },
    {
        id: 'coin',
        iconSlot: 'frogger.collectible.coin',
        color: 0xf59e0b,
        points: FROGGER_CONFIG.COIN_POINTS,
        titleKey: 'frogger.collect_coin_title',
        descKey: 'frogger.collect_coin_desc',
    },
];

const TITLE_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial', fontSize: 19, fontWeight: '800', fill: '#ffffff',
    dropShadow: { color: '#000000', distance: 1, blur: 2, alpha: 0.4 },
});

const DESC_STYLE = new TextStyle({
    fontFamily: 'Poppins, Arial', fontSize: 14, fontWeight: '600', fill: '#f1f5f9',
});

export class FroggerStartArea {
    /** Background strip (game-bottom.png + road-blue underlay). Sits BEHIND
     * the world so the player remains visible standing on it at game start. */
    container = new Container();
    /** Power-up cards (SPRINT / BOMBA / LASSITAS). Sits ABOVE the world so
     * scrolling road lanes never cover the UI cards. */
    cardsContainer = new Container();
    _game = null;

    /**
     * @param {*} game
     */
    constructor(game) {
        this._game = game;
        this._build();
    }

    _build() {
        const screenW = this._game.width;
        const screenH = this._game.height;
        const stripY = screenH - STRIP_HEIGHT;

        // Always paint a road-blue base behind the bottom strip. This kills the
        // light-blue sky bleed that otherwise shows through any transparent
        // pixels in `frogger.bottom` (e.g. the alpha edge above the grass), so
        // the strip blends seamlessly into the road lanes above it.
        const base = new Graphics();
        base.rect(0, stripY, screenW, STRIP_HEIGHT);
        base.fill({ color: FROGGER_CONFIG.COLOR_ROAD, alpha: 1 });
        this.container.addChild(base);

        const sprite = this._game.theme?.createSprite(
            'frogger.bottom', screenW, STRIP_HEIGHT,
            { anchorX: 0, anchorY: 0, fitMode: 'cover' }
        );
        if (sprite) {
            sprite.x = 0;
            sprite.y = stripY;
            this.container.addChild(sprite);
        } else {
            const fallback = new Graphics();
            fallback.rect(0, stripY, screenW, STRIP_HEIGHT);
            fallback.fill({ color: 0xb2c63c, alpha: 1 });
            this.container.addChild(fallback);
        }

        // Combined row: DASH / BOMB / SLOW power-ups (320px each) +
        // STAR / COIN collectible cards (240px each). Layout is a
        // single left-to-right run with per-card widths so the five
        // cards stay centred together on the bottom strip regardless
        // of how many of each category we ship in the future.
        const cards = [
            ...POWER_UPS.map((def) => ({ def, width: CARD_W })),
            ...COLLECTIBLES.map((def) => ({ def, width: COLLECT_CARD_W })),
        ];
        const totalW = cards.reduce((sum, c) => sum + c.width, 0)
            + (cards.length - 1) * CARD_GAP;
        const startX = (screenW - totalW) / 2;
        const cardsY = screenH - CARD_H - CARDS_BOTTOM_PAD;

        let cursorX = startX;
        for (const { def, width } of cards) {
            const card = this._buildCard(def, width);
            card.x = cursorX;
            card.y = cardsY;
            this.cardsContainer.addChild(card);
            cursorX += width + CARD_GAP;
        }
    }

    _buildCard(def, width = CARD_W) {
        const card = new Container();

        // v2 bevel stack — keeps the dark rim + bevel highlight + faded
        // white inner stroke that gives the card depth, but the drop
        // shadow is dropped (`shadowAlpha: 0`) so the strip reads as a
        // flat panel pinned to the bottom of the play field instead of
        // floating cards. Mirrors the same `shadowAlpha: 0` choice in
        // `FroggerTopBar`'s chips so the whole Frogger UI is shadow-less.
        const face = paintChip({
            w: width,
            h: CARD_H,
            r: CARD_RADIUS,
            color: def.color,
            shadowAlpha: 0,
        });
        card.addChild(face);

        // White medallion behind the power-up icon, ringed in a darker
        // tone of the card's own colour for that "stamped icon" look.
        const rim = darken(def.color, 0.55);
        const iconBg = new Graphics();
        iconBg.circle(ICON_CX, CARD_H / 2, ICON_SIZE / 2 + 5);
        iconBg.fill({ color: 0xffffff, alpha: 0.95 });
        iconBg.stroke({ color: rim, width: 3 });
        card.addChild(iconBg);

        const icon = this._game.theme?.createSprite(def.iconSlot, ICON_SIZE, ICON_SIZE, {
            anchorX: 0.5, anchorY: 0.5, fitMode: 'contain',
        });
        if (icon) {
            icon.x = ICON_CX;
            icon.y = CARD_H / 2;
            card.addChild(icon);
        }

        // Power-up cards stick to the legacy `frogger.power_<id>_*` keys
        // and have no point variable. Collectible cards opt in to
        // explicit `titleKey` / `descKey` + a `{p}` interpolation so the
        // point value tracks `FROGGER_CONFIG` instead of being hard-coded
        // in the translation string.
        const titleKey = def.titleKey || `frogger.power_${def.id}_title`;
        const descKey = def.descKey || `frogger.power_${def.id}_desc`;
        const vars = def.points != null ? { p: def.points } : undefined;

        const title = new Text({ text: t(titleKey, vars), style: TITLE_STYLE });
        title.x = TEXT_X;
        title.y = 15;
        card.addChild(title);

        const desc = new Text({ text: t(descKey, vars), style: DESC_STYLE });
        desc.x = TEXT_X;
        desc.y = 46;
        card.addChild(desc);

        return card;
    }

    /**
     * Call once per frame with the player's current world row. The strip
     * fades out between FADE_START_ROW and FADE_END_ROW so it doesn't stick
     * around for the whole run.
     */
    update(playerWorldRow) {
        if (!Number.isFinite(FADE_START_ROW) || !Number.isFinite(FADE_END_ROW)) {
            this.container.alpha = 1;
            this.container.visible = true;
            this.cardsContainer.alpha = 1;
            this.cardsContainer.visible = true;
            return;
        }
        const span = Math.max(1, FADE_END_ROW - FADE_START_ROW);
        const alpha = 1 - Math.max(0, Math.min(1, (playerWorldRow - FADE_START_ROW) / span));
        this.container.alpha = alpha;
        this.container.visible = alpha > 0.01;
        this.cardsContainer.alpha = alpha;
        this.cardsContainer.visible = alpha > 0.01;
    }

    destroy() {
        this.container.removeChildren();
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.cardsContainer.removeChildren();
        if (this.cardsContainer.parent) this.cardsContainer.parent.removeChild(this.cardsContainer);
        this._game = null;
    }
}
